import { useEffect, useRef } from 'react';
import { COLORS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import { APP_ROLE_PREFIX } from '../constants/auth-methods';
import type { KeycloakRole } from '../api/keycloak-admin';

interface RoleDropdownProps {
  userId: string;
  appRoles: KeycloakRole[];
  selectedRoles: Set<string>;
  onToggle: (userId: string, roleName: string) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  disabled: boolean;
  isDark: boolean;
  colors: ColorTheme;
}

/** Multi-select dropdown for assigning app roles */
export function RoleDropdown({
  userId,
  appRoles,
  selectedRoles,
  onToggle,
  isOpen,
  onOpenChange,
  disabled,
  isDark,
  colors,
}: RoleDropdownProps): React.JSX.Element {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedCount = selectedRoles.size;
  const label = selectedCount === 0
    ? 'Select apps...'
    : selectedCount === appRoles.length
      ? 'All apps'
      : `${selectedCount} app${selectedCount > 1 ? 's' : ''} selected`;

  /** Close dropdown when clicking outside */
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onOpenChange]);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Trigger button */}
      <button
        onClick={() => onOpenChange(!isOpen)}
        disabled={disabled}
        style={{
          height: '34px',
          padding: '0 12px',
          borderRadius: '6px',
          border: `1px solid ${selectedCount > 0 ? COLORS.accent.blue : colors.border}`,
          backgroundColor: selectedCount > 0
            ? (isDark ? 'rgba(1, 169, 237, 0.1)' : 'rgba(1, 169, 237, 0.05)')
            : 'transparent',
          color: selectedCount > 0 ? COLORS.accent.blue : colors.textSecondary,
          fontFamily: FONTS.body,
          fontSize: '12px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: '140px',
        }}
      >
        {label}
        <span style={{ fontSize: '8px', marginLeft: 'auto' }}>{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            backgroundColor: isDark ? '#333' : '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            zIndex: 10,
            minWidth: '180px',
            padding: '4px 0',
          }}
        >
          {/* Select All / None */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '6px 12px',
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <button
              onClick={() => appRoles.forEach(r => { if (!selectedRoles.has(r.name)) onToggle(userId, r.name); })}
              style={{
                background: 'none', border: 'none', color: COLORS.accent.blue,
                fontFamily: FONTS.body, fontSize: '11px', cursor: 'pointer', padding: 0,
              }}
            >
              Select All
            </button>
            <button
              onClick={() => appRoles.forEach(r => { if (selectedRoles.has(r.name)) onToggle(userId, r.name); })}
              style={{
                background: 'none', border: 'none', color: colors.textSecondary,
                fontFamily: FONTS.body, fontSize: '11px', cursor: 'pointer', padding: 0,
              }}
            >
              Clear
            </button>
          </div>

          {/* Role options */}
          {appRoles.map(role => {
            const isSelected = selectedRoles.has(role.name);
            const displayName = role.name.replace(APP_ROLE_PREFIX, '');
            return (
              <button
                key={role.name}
                onClick={() => onToggle(userId, role.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 12px',
                  background: isSelected
                    ? (isDark ? 'rgba(1, 169, 237, 0.1)' : 'rgba(1, 169, 237, 0.05)')
                    : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: FONTS.body,
                  fontSize: '13px',
                  color: colors.text,
                }}
              >
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '3px',
                    border: `1.5px solid ${isSelected ? COLORS.accent.blue : colors.border}`,
                    backgroundColor: isSelected ? COLORS.accent.blue : 'transparent',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: '10px',
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  {isSelected ? '\u2713' : ''}
                </span>
                {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
